import React, { useEffect, useState } from "react";
import { useAppServices } from "../../../../../../hook/services";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { MMStyles } from "../../../EditMenus/components/Modals/AddNewMenuModal";

/* ------------------------------------------------------------------ */
/*  Defaults & helpers                                                 */
/* ------------------------------------------------------------------ */

// `selector` = the GHL launchpad tab's data-name, used by the script to target
// the matching category tab (e.g. document.querySelector('[data-name="..."]')).
// Font Awesome 5 solid classes — FA5 Free is loaded on GHL and the admin loads
// FA 5.15.4, so the same class renders in both. The script injects an <i> with
// this class in place of GHL's native SVG icon.
export const DEFAULT_CATEGORIES = [
  { id: "foundational", selector: "Foundational setup", name: "Foundational setup", icon: "fas fa-sun" },
  { id: "marketing", selector: "Marketing & lead generation", name: "Marketing & lead generation", icon: "fas fa-paper-plane" },
  { id: "sales", selector: "Sales & conversations", name: "Sales & conversations", icon: "fas fa-comment-dots" },
  { id: "website", selector: "Website & monetization", name: "Website & monetization", icon: "fas fa-globe" },
  { id: "ecommerce", selector: "Ecommerce", name: "Ecommerce", icon: "fas fa-shopping-cart" },
];

const CATEGORY_ICONS = [
  "fas fa-sun", "fas fa-paper-plane", "fas fa-chart-pie", "fas fa-globe", "fas fa-shopping-cart",
  "fas fa-users", "fas fa-bullhorn", "fas fa-chart-line", "fas fa-comment-dots",
  "fas fa-rocket", "fas fa-star", "fas fa-shopping-bag", "fas fa-envelope",
  "fas fa-credit-card", "fas fa-calendar-alt", "fas fa-cog",
];

const TASK_ICONS = [
  "fas fa-user-plus", "fas fa-users", "fas fa-filter", "fas fa-comment-alt", "fas fa-envelope",
  "fas fa-sitemap", "fas fa-magnet", "fas fa-comments", "fas fa-calendar-check", "fas fa-comment-dots",
  "fas fa-window-maximize", "fas fa-tag", "fas fa-graduation-cap", "fas fa-credit-card",
  "fas fa-link", "fas fa-receipt", "fas fa-star", "fas fa-rocket", "fas fa-bullseye", "fas fa-thumbs-up",
];

const uid = () => `${Date.now()}-${Math.floor(Math.random() * 1e6)}`;

const normalizeTask = (t = {}) => ({
  id: t.id || uid(),
  title: t.title || "",
  description: t.description || "",
  // `selector` = the GHL launchpad accordion item id (hr-collapse-item id),
  // used by the injection script to target the matching task in the DOM.
  selector: t.selector || "",
  // `type` tells the script whether this maps to a native GHL item ("default",
  // update in place) or is agency-added ("new", inject into the DOM).
  type: t.type === "new" ? "new" : "default",
  icon: t.icon || "",
  imageUrl: t.imageUrl || "",
  progress: Number(t.progress) || 0,
  tutorialUrl: t.tutorialUrl || "",
  buttonLink: t.buttonLink || "",
});

// Default starter tasks per category — mirrors the standard Setup Guide.
const DEFAULT_TASKS = {
  foundational: [
    { selector: "Create a new contact", icon: "fas fa-user-plus", title: "Create a new contact", description: "Add your first contact effortlessly and begin building meaningful engagement right away." },
    { selector: "Import and engage with all your contacts instantly", icon: "fas fa-users", title: "Import and engage with all your contacts instantly", description: "Import your existing contacts from various platforms and start engaging with them immediately. Bring all your audience into one place and kickstart your outreach." },
    { selector: "Generate new leads with a high-converting funnel", icon: "fas fa-filter", title: "Generate new leads with a high-converting funnel", description: "Set up a high-converting funnel and form to capture leads efficiently. Optimize your lead generation and grow your customer base." },
    { selector: "Set up multi-channel communication & start engaging in minutes", icon: "fas fa-comment-alt", title: "Set up multi-channel communication & start engaging in minutes", description: "Connect your email, SMS, and phone services in minutes and start multi-channel communication. Improve your response rates and customer engagement quickly." },
    { selector: "Launch your first campaign to drive engagement", icon: "fas fa-envelope", title: "Launch your first campaign to drive engagement", description: "Design and send your first email campaign to engage over 1,000 contacts. Promote your services, stay top of mind, and drive conversions with targeted emails." },
    { selector: "Nurture leads with automated drip campaigns", icon: "fas fa-sitemap", title: "Nurture leads with automated drip campaigns", description: "Set up automated email and SMS drip campaigns to nurture 100+ leads over time. Increase conversion rates by following up consistently without manual intervention." },
    { selector: "Book more appointments with automated scheduling", icon: "fas fa-calendar-check", title: "Book more appointments with automated scheduling", description: "Set up your calendar for automatic appointment bookings. Secure 20 appointments in the first week, with 25% fewer no-shows through automated reminders." },
    { selector: "Accelerate deal closures with a streamlined sales pipeline", icon: "fas fa-columns", title: "Accelerate deal closures with a streamlined sales pipeline", description: "Organize your sales pipeline to track every lead's progress. Automate follow-ups and close deals faster, ensuring no opportunity is missed." },
  ],
  marketing: [
    { selector: "Manage all your social media posts in one place", icon: "fas fa-comment-alt", title: "Manage all your social media posts in one place", description: "Set up the social planner to schedule and manage all your social media posts. Maintain a consistent presence across platforms like Facebook, Instagram, and more." },
    { selector: "Capture new leads with Facebook & Instagram lead ads", icon: "fas fa-magnet", title: "Capture new leads with Facebook & Instagram lead ads", description: "Connect your Facebook and Instagram channels to manage lead generation ads. Capture high-quality leads directly from social media." },
    { selector: "Reach customers with your first SMS campaign", icon: "fas fa-comments", title: "Reach customers with your first SMS campaign", description: "Design and launch an SMS campaign to instantly reach your audience. Engage customers directly with personalised messages." },
  ],
  sales: [
    { selector: "Engage website visitors in real time with live chat", icon: "fas fa-comment-dots", title: "Engage website visitors in real time with live chat", description: "Add live chat to your website to provide instant support and engage visitors in real time. Convert visitors into leads by offering immediate assistance." },
    { selector: "Streamline Facebook & Instagram conversations for faster response", icon: "fas fa-comments", title: "Streamline Facebook & Instagram conversations for faster response", description: "Connect Facebook and Instagram Messenger to your CRM to manage all conversations in one place. Streamline responses and engage customers faster for improved communication efficiency" },
  ],
  website: [
    { selector: "Launch a professional website in less than a day", icon: "fas fa-window-maximize", title: "Launch a professional website in less than a day", description: "Create and publish a custom website using ready-made templates. Build your online presence quickly and easily." },
    { selector: "Migrate your WordPress site seamlessly", icon: "fas fa-window-maximize", title: "Migrate your WordPress site seamlessly", description: "Learn how to move your existing WordPress website to the platform easily without losing any content or functionality." },
  ],
  ecommerce: [
    { selector: "Launch your first product and start selling", icon: "fas fa-tag", title: "Launch your first product and start selling", description: "Set up and publish your product to start generating sales. Whether it's a physical product, digital service, or subscription, your online store is ready." },
    { selector: "Launch your first online course and start earning", icon: "fas fa-graduation-cap", title: "Launch your first online course and start earning", description: "Set up and launch an online course in minutes. Start monetizing your expertise and grow your audience with educational content." },
    { selector: "Start collecting payments with Stripe", icon: "fas fa-credit-card", title: "Start collecting payments with Stripe", description: "Connect your Stripe account for fast and secure payments. Enable credit card transactions, subscriptions, and more." },
    { selector: "Accept payments easily with a custom payment link", icon: "fas fa-link", title: "Accept payments easily with a custom payment link", description: "Create a payment link and send it to your customers for easy, secure transactions." },
    { selector: "Generate and send professional invoices", icon: "fas fa-receipt", title: "Generate and send professional invoices", description: "Manage your sales and billing by sending customized invoices to your customers." },
  ],
};

const buildDefaultGuide = () =>
  DEFAULT_CATEGORIES.map((c) => ({
    ...c,
    type: "default",
    tasks: (DEFAULT_TASKS[c.id] || []).map((t) => normalizeTask({ ...t, type: "default" })),
  }));

// The launchpads array can hold either old-style flat tasks or new-style
// categories (each with a nested `tasks` array). Normalise to categories.
const toCategories = (launchpads) => {
  const list = Array.isArray(launchpads) ? launchpads : [];
  const categoryShaped = list.length > 0 && list.every((i) => i && Array.isArray(i.tasks));

  if (categoryShaped) {
    return list.map((c) => ({
      id: c.id || uid(),
      selector: c.selector || "",
      type: c.type === "new" ? "new" : "default",
      name: c.name || "Untitled",
      icon: c.icon || "fas fa-star",
      tasks: (c.tasks || []).map(normalizeTask),
    }));
  }

  // Not category-shaped yet (empty record, or old classic flat tasks):
  // seed the full default Setup Guide from the images.
  return buildDefaultGuide();
};

/* ------------------------------------------------------------------ */
/*  Small UI atoms                                                     */
/* ------------------------------------------------------------------ */

const ProgressRing = ({ value }) => {
  const r = 11;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(100, Math.max(0, value)) / 100) * circ;
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" className="np-ring">
      <circle cx="14" cy="14" r={r} fill="none" stroke="#e5e9f0" strokeWidth="3" />
      <circle
        cx="14" cy="14" r={r} fill="none" stroke="#2563eb" strokeWidth="3"
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        transform="rotate(-90 14 14)"
      />
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/*  Category add / edit modal                                          */
/* ------------------------------------------------------------------ */

// Shared modal shell reusing the app's "Add menu" modal styles (mm-*).
const LpModal = ({ title, subtitle, submitLabel, submitting, onClose, onSubmit, children }) => (
  <div className="mm-overlay" onMouseDown={onClose}>
    <div
      className="mm-dialog"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <header className="mm-header d-flex justify-content-between align-items-center">
        <div>
          <h5 className="mm-title mb-0">{title}</h5>
          {subtitle && <p className="mm-subtitle mb-0">{subtitle}</p>}
        </div>
        <button type="button" className="mm-close" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </header>

      <div className="mm-body">{children}</div>

      <footer className="mm-footer d-flex justify-content-end gap-2">
        <button type="button" className="mm-btn mm-btn--ghost" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button type="button" className="mm-btn mm-btn--primary" onClick={onSubmit} disabled={submitting}>
          {submitLabel}
        </button>
      </footer>

      <MMStyles />
    </div>
  </div>
);

const CategoryModal = ({ initial, onClose, onSave }) => {
  const [name, setName] = useState(initial?.name || "");
  const [icon, setIcon] = useState(initial?.icon || CATEGORY_ICONS[0]);

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ name: name.trim(), icon });
  };

  return (
    <LpModal
      title={initial ? "Edit category" : "Add category"}
      subtitle="Group your setup tasks under a category."
      submitLabel={initial ? "Save changes" : "Add category"}
      onClose={onClose}
      onSubmit={submit}
    >
      <div className="mm-field">
        <label className="mm-label">Category name</label>
        <input
          className="mm-input" value={name} autoFocus
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Foundational Setup"
        />
      </div>
      <div className="mm-field">
        <label className="mm-label">Icon</label>
        <div className="mm-icon-grid">
          <div className="mm-icon-grid-inner">
            {CATEGORY_ICONS.map((ic) => (
              <button
                type="button" key={ic}
                className={`mm-icon-cell ${icon === ic ? "is-selected" : ""}`}
                onClick={() => setIcon(ic)}
                title={ic}
              >
                <i className={ic}></i>
              </button>
            ))}
          </div>
        </div>
      </div>
    </LpModal>
  );
};

/* ------------------------------------------------------------------ */
/*  Task add / edit modal                                              */
/* ------------------------------------------------------------------ */

const TaskModal = ({ initial, onClose, onSave }) => {
  const [form, setForm] = useState(() => normalizeTask(initial || {}));
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.title.trim() && !form.description.trim()) return;
    onSave({ ...form, progress: Number(form.progress) || 0 });
  };

  return (
    <LpModal
      title={initial ? "Edit task" : "Add task"}
      subtitle="Add a step to your setup guide."
      submitLabel={initial ? "Save changes" : "Add task"}
      onClose={onClose}
      onSubmit={submit}
    >
      <div className="mm-field">
        <label className="mm-label">Title</label>
        <input className="mm-input" value={form.title} onChange={set("title")} autoFocus placeholder="e.g. Create a New Contact" />
      </div>
      <div className="mm-field">
        <label className="mm-label">Description</label>
        <textarea
          className="mm-input"
          style={{ height: "auto", minHeight: 72, padding: "9px 12px", resize: "vertical" }}
          rows={2}
          value={form.description}
          onChange={set("description")}
        />
      </div>
      <div className="mm-field">
        <label className="mm-label">Icon</label>
        <div className="mm-icon-grid">
          <div className="mm-icon-grid-inner">
            {TASK_ICONS.map((ic) => (
              <button
                type="button" key={ic}
                className={`mm-icon-cell ${form.icon === ic ? "is-selected" : ""}`}
                onClick={() => setForm((p) => ({ ...p, icon: ic, imageUrl: "" }))}
                title={ic}
              >
                <i className={ic}></i>
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="mm-field">
        <label className="mm-label">Tutorial video URL (optional)</label>
        <input className="mm-input" value={form.tutorialUrl} onChange={set("tutorialUrl")} placeholder="https://" />
      </div>
      <div className="mm-field">
        <label className="mm-label">Button link (optional)</label>
        <input className="mm-input" value={form.buttonLink} onChange={set("buttonLink")} placeholder="https://" />
      </div>
    </LpModal>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

const NewLaunchPad = ({ tasks, data, onRefresh }) => {
  const AppService = useAppServices();
  const { companyId, type } = useParams();
  const handler =
    type === "agency" || type === "location"
      ? AppService.sidenav_customizer_main_level
      : type === "group"
      ? AppService.sidenav_customizer_custom_level
      : AppService.sidenav_customizer_saas_level;

  const [categories, setCategories] = useState(() => toCategories(tasks));
  const [activeId, setActiveId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);

  const [catModal, setCatModal] = useState(null);   // { mode, category }
  const [taskModal, setTaskModal] = useState(null);  // { mode, task }
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  // Re-sync whenever the parent reloads the record.
  useEffect(() => {
    setCategories(toCategories(tasks));
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const active =
    categories.find((c) => c.id === activeId) || categories[0] || null;

  useEffect(() => {
    if (categories.length && !categories.some((c) => c.id === activeId)) {
      setActiveId(categories[0].id);
    }
  }, [categories, activeId]);

  /* ---- persistence -------------------------------------------------- */
  const persist = async (nextCategories) => {
    setCategories(nextCategories); // optimistic
    setSaving(true);
    const payload = {
      launchpads: nextCategories,
      launchpad_type: "new",
      companyId,
      type,
      ...(data?._id && { _id: data._id }),
    };
    const { response } = await handler[data?._id ? "update" : "create"]({ payload });
    setSaving(false);
    if (response) {
      onRefresh && onRefresh();
    } else {
      toast.error("Failed to save changes");
    }
  };

  /* ---- category ops ------------------------------------------------- */
  const saveCategory = ({ name, icon }) => {
    let next;
    if (catModal?.mode === "edit") {
      next = categories.map((c) =>
        c.id === catModal.category.id ? { ...c, name, icon } : c
      );
    } else {
      next = [...categories, { id: uid(), selector: "", type: "new", name, icon, tasks: [] }];
    }
    setCatModal(null);
    persist(next);
  };

  const deleteCategory = (id) => {
    if (!window.confirm("Delete this category and its tasks?")) return;
    persist(categories.filter((c) => c.id !== id));
  };

  const resetCategories = () => setShowResetConfirm(true);

  const confirmReset = () => {
    setShowResetConfirm(false);
    persist(buildDefaultGuide());
  };

  /* ---- task ops ----------------------------------------------------- */
  const saveTask = (task) => {
    const next = categories.map((c) => {
      if (c.id !== active.id) return c;
      if (taskModal?.mode === "edit") {
        return { ...c, tasks: c.tasks.map((t) => (t.id === task.id ? task : t)) };
      }
      return { ...c, tasks: [...c.tasks, { ...task, id: uid(), type: "new" }] };
    });
    setTaskModal(null);
    persist(next);
  };

  const deleteTask = (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    persist(
      categories.map((c) =>
        c.id === active.id ? { ...c, tasks: c.tasks.filter((t) => t.id !== taskId) } : c
      )
    );
  };

  const activeTasks = active?.tasks || [];
  const progressValues = activeTasks.map((t) => Number(t.progress) || 0);
  const categoryProgress = progressValues.length
    ? Math.round(progressValues.reduce((a, b) => a + b, 0) / progressValues.length)
    : 0;

  return (
    <div className="np-wrap">
      <style>{css}</style>
      <MMStyles />

      {/* Sidebar */}
      <aside className="np-side">
        <div className="np-side-head">
          <span className="np-side-title">Setup Guide</span>
          <button className="np-side-add" title="Add category" onClick={() => setCatModal({ mode: "add" })}>
            <i className="bi bi-plus-lg"></i>
          </button>
        </div>

        <div className="np-nav">
          {categories.map((cat) => (
            <div
              key={cat.id}
              className={`np-nav-item ${active?.id === cat.id ? "active" : ""}`}
              onClick={() => { setActiveId(cat.id); setExpanded(null); }}
            >
              <i className={`${cat.icon} np-nav-icon`}></i>
              <span className="np-nav-label">{cat.name}</span>
              <span className="np-nav-actions" onClick={(e) => e.stopPropagation()}>
                <button title="Edit" onClick={() => setCatModal({ mode: "edit", category: cat })}>
                  <i className="bi bi-pencil"></i>
                </button>
                {cat.type === "new" && (
                  <button title="Delete" onClick={() => deleteCategory(cat.id)}>
                    <i className="bi bi-trash"></i>
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>

        <button className="np-reset" onClick={resetCategories}>
          <i className="bi bi-arrow-counterclockwise"></i> Reset to default
        </button>
      </aside>

      {/* Main */}
      <section className="np-main">
        <div className="np-main-head">
          {/* <h3 className="np-greeting">
            Hey {data?.name || "there"}, here are few things you can get started with
          </h3> */}
          {active && (
            <button
              type="button"
              className="mm-trigger mm-trigger--primary d-inline-flex align-items-center"
              onClick={() => setTaskModal({ mode: "add" })}
            >
              <span className="mm-trigger-icon d-inline-flex align-items-center justify-content-center" aria-hidden="true">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </span>
              <span className="mm-trigger-label">Add task</span>
            </button>
          )}
        </div>

        {active && (
          <>
            <div className="np-progress-label">Your {active.name} Progress.</div>
            <div className="np-progress-row">
              <div className="np-progress-track">
                <div className="np-progress-fill" style={{ width: `${categoryProgress}%` }} />
              </div>
              <span className="np-progress-pct">{categoryProgress}%</span>
            </div>

            <div className="np-card">
              {activeTasks.length === 0 && (
                <div className="np-empty">
                  <i className="bi bi-clipboard-plus"></i>
                  <p>No tasks in this category yet.</p>
                  <button className="btn btn-sm btn-outline-primary" onClick={() => setTaskModal({ mode: "add" })}>
                    Add your first task
                  </button>
                </div>
              )}

              {activeTasks.map((task) => {
                const open = expanded === task.id;
                const heading = task.title || task.description || "Untitled task";
                const sub = task.title ? task.description : "";
                return (
                  <div className={`np-item ${open ? "open" : ""}`} key={task.id}>
                    <div className="np-item-head">
                      <span className="np-item-icon">
                        {task.imageUrl ? (
                          <img src={task.imageUrl} alt="" />
                        ) : (
                          <i className={task.icon || "fas fa-star"}></i>
                        )}
                      </span>
                      <div className="np-item-body" onClick={() => setExpanded(open ? null : task.id)}>
                        <div className="np-item-title">{heading}</div>
                        {sub && <p className="np-item-desc">{sub}</p>}
                      </div>
                      <div className="np-item-meta">
                        <span className="np-ring-wrap">
                          <ProgressRing value={task.progress} />
                          <span className="np-ring-pct">{Number(task.progress) || 0}%</span>
                        </span>
                        <button className="np-icon-btn" title="Edit" onClick={() => setTaskModal({ mode: "edit", task })}>
                          <i className="bi bi-pencil"></i>
                        </button>
                        {task.type === "new" && (
                          <button className="np-icon-btn danger" title="Delete" onClick={() => deleteTask(task.id)}>
                            <i className="bi bi-trash"></i>
                          </button>
                        )}
                        <button className="np-icon-btn" title="Toggle" onClick={() => setExpanded(open ? null : task.id)}>
                          <i className={`bi ${open ? "bi-chevron-up" : "bi-chevron-right"}`}></i>
                        </button>
                      </div>
                    </div>

                    {open && (
                      <div className="np-item-expanded">
                        {task.tutorialUrl ? (
                          <>
                            <div className="np-tutorial-label">Watch the Tutorial</div>
                            <a className="np-tutorial-card" href={task.tutorialUrl} target="_blank" rel="noreferrer">
                              <span className="np-tutorial-title">{heading}</span>
                              <span className="np-play"><i className="bi bi-play-fill"></i></span>
                            </a>
                          </>
                        ) : (
                          <p className="np-no-tutorial">No tutorial added for this task.</p>
                        )}
                        {task.buttonLink && (
                          <a className="np-task-link" href={task.buttonLink} target="_blank" rel="noreferrer">
                            Open link <i className="bi bi-box-arrow-up-right"></i>
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {saving && <div className="np-saving">Saving…</div>}
      </section>

      {catModal && (
        <CategoryModal
          initial={catModal.mode === "edit" ? catModal.category : null}
          onClose={() => setCatModal(null)}
          onSave={saveCategory}
        />
      )}
      {taskModal && (
        <TaskModal
          initial={taskModal.mode === "edit" ? taskModal.task : null}
          onClose={() => setTaskModal(null)}
          onSave={saveTask}
        />
      )}

      {showResetConfirm && (
        <>
          <div
            className="modal fade show"
            style={{ display: "block" }}
            tabIndex={-1}
            role="dialog"
            onClick={() => setShowResetConfirm(false)}
          >
            <div
              className="modal-dialog modal-dialog-centered"
              role="document"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Reset to default?</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowResetConfirm(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    This replaces your current setup guide with the default
                    configuration. Your changes will be lost.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowResetConfirm(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={confirmReset}
                  >
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */

const css = `
.np-wrap { display:flex; gap:28px; align-items:flex-start; }

.np-side { width:256px; flex-shrink:0; position:sticky; top:16px; }
.np-side-head { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.np-side-title { font-size:13px; font-weight:700; letter-spacing:.3px; color:#0f172a; text-transform:uppercase; }
.np-side-add { width:26px; height:26px; border-radius:7px; border:1px solid #e2e8f0; background:#fff; color:#2563eb; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; font-size:13px; }
.np-side-add:hover { background:#eff6ff; }
.np-nav { display:flex; flex-direction:column; gap:4px; }
.np-nav-item { display:flex; align-items:center; gap:11px; padding:11px 12px; border-radius:11px; cursor:pointer; color:#475569; font-size:14px; font-weight:500; transition:background .12s ease,color .12s ease,box-shadow .12s ease; }
.np-nav-item:hover { background:#f1f5f9; }
.np-nav-item.active { background:#fff; color:#2563eb; font-weight:600; box-shadow:0 1px 4px rgba(15,23,42,.10); }
.np-nav-icon { font-size:16px; width:18px; text-align:center; }
.np-nav-label { flex:1; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.np-nav-actions { display:none; gap:2px; }
.np-nav-item:hover .np-nav-actions { display:inline-flex; }
.np-nav-actions button { border:none; background:transparent; color:#94a3b8; cursor:pointer; padding:2px 4px; font-size:12.5px; border-radius:5px; }
.np-nav-actions button:hover { color:#2563eb; background:#eff6ff; }
.np-reset { margin-top:16px; width:100%; border:1px dashed #cbd5e1; background:transparent; color:#64748b; border-radius:10px; padding:9px; font-size:13px; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; gap:7px; }
.np-reset:hover { color:#dc2626; border-color:#fca5a5; background:#fef2f2; }

.np-main { flex:1; min-width:0; }
.np-main-head { display:flex; align-items:flex-start; justify-content:space-between; gap:14px; flex-wrap:wrap; margin-bottom:20px; }
.np-greeting { font-size:19px; font-weight:700; color:#0f172a; margin:0; }
.np-add-task { border:none; background:#2563eb; color:#fff; border-radius:10px; padding:9px 16px; font-size:13.5px; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:7px; flex-shrink:0; }
.np-add-task:hover { background:#1d4ed8; }

.np-progress-label { font-size:14px; color:#475569; margin-bottom:9px; }
.np-progress-row { display:flex; align-items:center; gap:14px; margin-bottom:22px; }
.np-progress-track { flex:1; height:8px; border-radius:999px; background:#e5e9f0; overflow:hidden; }
.np-progress-fill { height:100%; background:#2563eb; border-radius:999px; transition:width .3s ease; }
.np-progress-pct { font-size:14px; font-weight:700; color:#475569; min-width:40px; text-align:right; }

.np-card { border:1px solid #eef2f7; border-radius:16px; overflow:hidden; background:#fff; }
.np-item { border-bottom:1px solid #f1f5f9; transition:background .12s ease; }
.np-item:last-child { border-bottom:none; }
.np-item.open { background:#f8fafc; }
.np-item-head { display:flex; align-items:flex-start; gap:15px; padding:16px 20px; }
.np-item-icon { width:38px; height:38px; border-radius:9px; flex-shrink:0; display:flex; align-items:center; justify-content:center; background:#f1f5f9; color:#64748b; font-size:17px; overflow:hidden; }
.np-item-icon img { width:22px; height:22px; object-fit:contain; }
.np-item-body { flex:1; min-width:0; cursor:pointer; }
.np-item-title { font-size:14.5px; font-weight:600; color:#2563eb; margin-bottom:3px; }
.np-item-desc { font-size:13px; color:#64748b; margin:0; line-height:1.5; }
.np-item-meta { display:flex; align-items:center; gap:8px; flex-shrink:0; }
.np-ring-wrap { display:inline-flex; align-items:center; gap:6px; margin-right:4px; }
.np-ring { flex-shrink:0; }
.np-ring-pct { font-size:12.5px; color:#94a3b8; min-width:30px; }
.np-icon-btn { border:none; background:transparent; color:#94a3b8; cursor:pointer; padding:5px; border-radius:7px; font-size:13.5px; line-height:1; }
.np-icon-btn:hover { color:#2563eb; background:#eff6ff; }
.np-icon-btn.danger:hover { color:#dc2626; background:#fef2f2; }

.np-item-expanded { padding:2px 20px 18px 73px; }
.np-tutorial-label { font-size:13px; font-weight:600; color:#0f172a; margin-bottom:10px; }
.np-tutorial-card { position:relative; display:flex; align-items:center; justify-content:center; width:220px; height:130px; border-radius:12px; text-decoration:none; overflow:hidden; background:linear-gradient(135deg,#1e293b 0%,#4338ca 60%,#7c3aed 100%); }
.np-tutorial-title { position:absolute; top:14px; left:14px; right:14px; color:#fff; font-size:13px; font-weight:600; }
.np-play { width:42px; height:42px; border-radius:50%; background:rgba(255,255,255,.92); color:#1d4ed8; display:flex; align-items:center; justify-content:center; font-size:22px; }
.np-no-tutorial { font-size:13px; color:#94a3b8; margin:0; }
.np-task-link { display:inline-flex; align-items:center; gap:6px; margin-top:12px; font-size:13px; color:#2563eb; text-decoration:none; }

.np-empty { padding:46px 20px; text-align:center; color:#94a3b8; }
.np-empty i { font-size:30px; display:block; margin-bottom:10px; }
.np-empty p { font-size:13.5px; margin:0 0 12px; }

.np-saving { margin-top:12px; font-size:12.5px; color:#94a3b8; }

.np-icon-grid { display:grid; grid-template-columns:repeat(8,1fr); gap:8px; }
.np-icon-pick { aspect-ratio:1; border:1px solid #e2e8f0; background:#fff; border-radius:9px; color:#64748b; font-size:16px; cursor:pointer; display:flex; align-items:center; justify-content:center; }
.np-icon-pick:hover { border-color:#93c5fd; }
.np-icon-pick.active { border-color:#2563eb; background:#eff6ff; color:#2563eb; }
`;

export default NewLaunchPad;
